import type {
    SiteBuilderRichTextMark,
    SiteBuilderRichTextSegment,
} from "./siteBuilderTypes";

type ShopRichTextMark = SiteBuilderRichTextMark;
type ShopRichTextSegment = SiteBuilderRichTextSegment;

export type ShopInlineEnterAction = "hardBreak" | "insertBlock" | "itemBreak" | "native";

export type ShopInlineInsertionOptions = {
    accepts?: readonly string[];
    insertIndex: number;
    maxItems?: number;
    replaceCount?: number;
};

export function resolveShopInlineEnterAction(options: {
    altKey?: boolean;
    contentKey: string;
    ctrlKey?: boolean;
    metaKey?: boolean;
    multiline: boolean;
    shiftKey?: boolean;
}): ShopInlineEnterAction {
    if (options.altKey || options.ctrlKey || options.metaKey) {
        return "native";
    }

    if (options.shiftKey) {
        return "hardBreak";
    }

    if (options.contentKey === "items") {
        return "itemBreak";
    }

    return options.multiline ? "hardBreak" : "insertBlock";
}

export function canInsertShopInlineItems(
    currentCount: number,
    insertedTypes: readonly string[],
    options: Pick<ShopInlineInsertionOptions, "accepts" | "maxItems" | "replaceCount"> = {},
): boolean {
    if (options.accepts && insertedTypes.some((type) => !options.accepts?.includes(type))) {
        return false;
    }

    const replaceCount = Math.min(Math.max(0, options.replaceCount ?? 0), Math.max(0, currentCount));

    return typeof options.maxItems !== "number"
        || currentCount - replaceCount + insertedTypes.length <= options.maxItems;
}

export function insertShopInlineItems<T extends { type: string }>(
    items: readonly T[],
    insertedItems: readonly T[],
    options: ShopInlineInsertionOptions,
): T[] | null {
    const insertIndex = Math.max(0, Math.min(options.insertIndex, items.length));
    const replaceCount = Math.min(
        Math.max(0, options.replaceCount ?? 0),
        Math.max(0, items.length - insertIndex),
    );

    if (!canInsertShopInlineItems(items.length, insertedItems.map((item) => item.type), {
        accepts: options.accepts,
        maxItems: options.maxItems,
        replaceCount,
    })) {
        return null;
    }

    const nextItems = [...items];

    nextItems.splice(insertIndex, replaceCount, ...insertedItems);

    return nextItems;
}

function cloneMarks(marks: ShopRichTextMark | undefined): ShopRichTextMark | undefined {
    return marks && Object.keys(marks).length > 0 ? { ...marks } : undefined;
}

function marksEqual(left: ShopRichTextMark | undefined, right: ShopRichTextMark | undefined): boolean {
    return JSON.stringify(left ?? {}) === JSON.stringify(right ?? {});
}

function compactSegments(segments: ShopRichTextSegment[]): ShopRichTextSegment[] {
    const compacted: ShopRichTextSegment[] = [];

    for (const segment of segments) {
        if (segment.text === "") {
            continue;
        }

        const previous = compacted[compacted.length - 1];

        if (previous && marksEqual(previous.marks, segment.marks)) {
            previous.text += segment.text;
            continue;
        }

        compacted.push({
            ...(segment.marks ? { marks: cloneMarks(segment.marks) } : {}),
            text: segment.text,
        });
    }

    return compacted;
}

function segmentSlice(
    segments: readonly ShopRichTextSegment[],
    start: number,
    end: number,
): ShopRichTextSegment[] {
    const result: ShopRichTextSegment[] = [];
    let offset = 0;

    for (const segment of segments) {
        const segmentStart = offset;
        const segmentEnd = segmentStart + segment.text.length;

        offset = segmentEnd;

        if (segmentEnd <= start || segmentStart >= end) {
            continue;
        }

        const text = segment.text.slice(
            Math.max(0, start - segmentStart),
            Math.min(segment.text.length, end - segmentStart),
        );

        if (text !== "") {
            result.push({
                ...(segment.marks ? { marks: cloneMarks(segment.marks) } : {}),
                text,
            });
        }
    }

    return result;
}

function normalizedSegments(segments: readonly ShopRichTextSegment[]): ShopRichTextSegment[] {
    return segments
        .filter((segment) => typeof segment?.text === "string")
        .map((segment) => ({
            ...(segment.marks ? { marks: cloneMarks(segment.marks) } : {}),
            text: segment.text,
        }));
}

function marksAt(segments: ShopRichTextSegment[], index: number): ShopRichTextMark | undefined {
    let offset = 0;

    for (const segment of segments) {
        const end = offset + segment.text.length;

        if (index >= offset && index < end) {
            return cloneMarks(segment.marks);
        }

        offset = end;
    }

    return undefined;
}

export function reconcileSiteBuilderRichTextSegments(
    segments: readonly SiteBuilderRichTextSegment[],
    nextText: string,
): SiteBuilderRichTextSegment[] {
    const sourceSegments = normalizedSegments(segments);
    const previousText = sourceSegments.map((segment) => segment.text).join("");

    if (previousText === nextText) {
        return sourceSegments.length > 0 ? sourceSegments : [{ text: nextText }];
    }

    let prefixLength = 0;

    while (
        prefixLength < previousText.length
        && prefixLength < nextText.length
        && previousText[prefixLength] === nextText[prefixLength]
    ) {
        prefixLength += 1;
    }

    let suffixLength = 0;

    while (
        suffixLength < previousText.length - prefixLength
        && suffixLength < nextText.length - prefixLength
        && previousText[previousText.length - suffixLength - 1] === nextText[nextText.length - suffixLength - 1]
    ) {
        suffixLength += 1;
    }

    const previousSuffixStart = previousText.length - suffixLength;
    const nextSuffixStart = nextText.length - suffixLength;
    const insertedText = nextText.slice(prefixLength, nextSuffixStart);
    const prefix = segmentSlice(sourceSegments, 0, prefixLength);
    const suffix = segmentSlice(sourceSegments, previousSuffixStart, previousText.length);
    const insertionMarks = prefixLength > 0
        ? marksAt(sourceSegments, prefixLength - 1)
        : marksAt(sourceSegments, previousSuffixStart);
    const nextSegments = [
        ...prefix,
        ...(insertedText !== "" ? [{
            ...(insertionMarks ? { marks: insertionMarks } : {}),
            text: insertedText,
        }] : []),
        ...suffix,
    ];

    const compacted = compactSegments(nextSegments);

    return compacted.length > 0 ? compacted : [{ text: nextText }];
}

/**
 * Backwards-compatible Shop name. The reconciliation algorithm is target
 * neutral and is shared by the Web and Shop translation editors.
 */
export const reconcileShopRichTextSegments = reconcileSiteBuilderRichTextSegments;

export function splitShopRichTextSegments(
    segments: readonly ShopRichTextSegment[],
    deleteStart: number,
    deleteEnd: number,
): { after: ShopRichTextSegment[]; before: ShopRichTextSegment[] } {
    const sourceSegments = normalizedSegments(segments);
    const textLength = sourceSegments.reduce((length, segment) => length + segment.text.length, 0);
    const start = Math.max(0, Math.min(deleteStart, textLength));
    const end = Math.max(start, Math.min(deleteEnd, textLength));

    return {
        after: compactSegments(segmentSlice(sourceSegments, end, textLength)),
        before: compactSegments(segmentSlice(sourceSegments, 0, start)),
    };
}

export function splitShopSlashCommandText(
    fullText: string,
    deleteStart: number,
    deleteEnd: number,
): { afterText: string; beforeText: string } {
    const start = Math.max(0, Math.min(deleteStart, fullText.length));
    const end = Math.max(start, Math.min(deleteEnd, fullText.length));

    return {
        afterText: fullText.slice(end),
        beforeText: fullText.slice(0, start),
    };
}

export function normalizeShopInlineText(
    value: string,
    options: {
        preserveBoundaryLineBreaks?: boolean;
        preserveLineBreaks?: boolean;
    } = {},
): string {
    const normalized = value
        .replace(/\u00a0/g, " ")
        .replace(/\u200b/g, "")
        .replace(/\r\n?/g, "\n");

    if (options.preserveBoundaryLineBreaks) {
        return normalized.replace(/^[\t ]+|[\t ]+$/g, "");
    }

    const trimmed = normalized.trim();

    return options.preserveLineBreaks ? trimmed : trimmed.replace(/\s+/g, " ");
}
